/**
 * E-GTCC-VIEW-DPLANE1 — tab "Plan Builders".
 *
 * The three builders of the old console (Create-DB, Assignment, Move) plus
 * the plan history with its timeline.
 *
 * [Validate] is the only verb this view knows: it posts the plan to the
 * existing `POST .../plans/*` routes, which answer `mode: "dry_run"`. There
 * is deliberately no execute control anywhere in this file — execution stays
 * on the Owner-Go path of the old console until the Owner releases it for
 * GTCC. No approval, runner or execution client is imported (see
 * `api/dplane.ts`), so no such request can be built from here at all.
 */
import { useEffect, useState, type ReactNode } from "react";
import { Button, Stack, TextInput } from "@genius-tracker/design-system";
import { useLocale } from "@genius-tracker/i18n/react";
import { useQuery } from "@tanstack/react-query";
import { GtccCapHint } from "../../../GtccCapHint.js";

import type { DbOpsDatabasePurpose, DbOpsMovePlanType } from "../../../../db-ops/types.js";
import {
  fetchDplanePlans,
  fetchDplanePlanTimeline,
  validateDplaneAssignmentPlan,
  validateDplaneCreateDatabasePlan,
  validateDplaneMovePlan,
  DPLANE_PLANS_KEY,
  DPLANE_PLAN_LIMIT,
  type DbOpsPersistedAuditEvent,
  type DbOpsPersistedPlan,
  type DplaneResult,
} from "../../../api/dplane.js";
import { Dot, type CockpitTone } from "../../../ui/primitives.js";
import {
  asList,
  DplaneGate,
  DplanePanel,
  DPLANE_QUERY_OPTIONS,
  GRID_COLUMN_STYLE,
  HOST_STYLE,
  MONO_STYLE,
  Mono,
  SUB_STYLE,
  TABLE_CELL_STYLE,
  TABLE_HEAD_STYLE,
  TABLE_STYLE,
  TABLE_WRAP_STYLE,
  type DplaneBuilderId,
  type DplaneTabProps,
} from "../components/DplanePanels.js";
import { toneOfPlanStatus, toneOfRisk } from "../components/dplaneTone.js";

export interface PlansTabProps extends DplaneTabProps {
  /** Which builder the Health Center linked to, if any. */
  readonly focusBuilder?: DplaneBuilderId | undefined;
  /** Tenant the Health Center pre-fills into the Move builder. */
  readonly focusTenantId?: string | undefined;
}

const DATABASE_PURPOSES: ReadonlyArray<DbOpsDatabasePurpose> = [
  "platform",
  "tenant_pool",
  "tenant_dedicated",
  "ops",
];

const MOVE_TYPES: ReadonlyArray<DbOpsMovePlanType> = [
  "same_host_database_move",
  "cross_host_internal_vps_move",
  "external_vps_move",
];

const FORM_STYLE = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
  gap: "var(--gt-sys-space-2)",
} as const;

const SELECT_STYLE = {
  width: "100%",
  fontFamily: "var(--gt-sys-font-family-mono)",
  fontSize: "var(--gt-sys-font-size-xs)",
  color: "var(--gt-sys-text)",
  background: "var(--gt-sys-surface)",
  border: "1px solid var(--gt-sys-border)",
  borderRadius: "var(--gt-sys-radius-sm)",
  padding: "var(--gt-sys-space-2)",
} as const;

/** No shared-kit equivalent (see FleetPrimitives.tsx): the dot only, no label. */
function FleetDot(props: { tone: CockpitTone }): ReactNode {
  return <Dot tone={props.tone} />;
}

/** No shared-kit equivalent (see FleetPrimitives.tsx): a muted note in mono under a panel. */
function FleetNote(props: { children: ReactNode; testId?: string }): ReactNode {
  return (
    <p className="mono muted" data-testid={props.testId}>
      {props.children}
    </p>
  );
}

/** No shared-kit equivalent (see FleetPrimitives.tsx); "nicht messbar" in mono/muted. */
function NotMeasurable(props: { testId?: string }): ReactNode {
  const { t } = useLocale();
  return (
    <span className="mono muted" data-testid={props.testId}>
      {t("admin.gtcc.cockpit.infra.not_measurable")}
    </span>
  );
}

/** The dry-run outcome of one builder, rendered under its form. */
function ValidationOutcome(props: {
  result: DplaneResult<DbOpsPersistedPlan> | undefined;
  testId: string;
}): ReactNode {
  const { t } = useLocale();
  if (props.result === undefined) return null;
  if (!props.result.ok) {
    const failure = props.result;
    const text =
      failure.kind === "disabled"
        ? t("admin.gtcc.dplane.failure.disabled")
        : failure.kind === "forbidden"
          ? t("admin.gtcc.dplane.failure.forbidden")
          : failure.kind === "upstream"
            ? t("admin.gtcc.dplane.failure.upstream", { status: failure.httpStatus })
            : t("admin.gtcc.dplane.failure.transport");
    return (
      <p style={SUB_STYLE} role="alert" data-testid={`${props.testId}-failure`}>
        {text}
      </p>
    );
  }
  const plan = props.result.data;
  const blockers = plan.readiness.filter((signal) => signal.status === "critical").length;
  return (
    <p style={SUB_STYLE} role="status" data-testid={`${props.testId}-result`}>
      {t("admin.gtcc.dplane.builders.dry_run_result", {
        id: plan.id,
        status: t(`admin.gtcc.dplane.plan_status.${plan.status}`),
        risk: t(`admin.gtcc.dplane.risk.${plan.riskLevel}`),
        blockers,
      })}
    </p>
  );
}

function BuilderPanel(props: {
  title: string;
  note: string;
  testId: string;
  focused: boolean;
  children: ReactNode;
}): ReactNode {
  return (
    <div data-testid={`${props.testId}-anchor`} data-focused={props.focused ? "true" : "false"}>
      <DplanePanel title={props.title} note={props.note} testId={props.testId}>
        {props.children}
      </DplanePanel>
    </div>
  );
}

/* ---------------------------------------------------------- Create-DB */

function CreateDatabaseBuilder(props: { focused: boolean }): ReactNode {
  const { t } = useLocale();
  const [hostId, setHostId] = useState("");
  const [instanceId, setInstanceId] = useState("");
  const [name, setName] = useState("");
  const [purpose, setPurpose] = useState<DbOpsDatabasePurpose>("tenant_dedicated");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<DplaneResult<DbOpsPersistedPlan> | undefined>(undefined);

  const ready = hostId.trim() !== "" && instanceId.trim() !== "" && name.trim() !== "";

  async function onValidate(): Promise<void> {
    setBusy(true);
    const next = await validateDplaneCreateDatabasePlan({
      targetHostId: hostId.trim(),
      targetInstanceId: instanceId.trim(),
      proposedDatabaseName: name.trim(),
      proposedPurpose: purpose,
    });
    setResult(next);
    setBusy(false);
  }

  return (
    <BuilderPanel
      title={t("admin.gtcc.dplane.builders.create")}
      note={t("admin.gtcc.dplane.builders.create.hint")}
      testId="gtcc-dplane-builder-create"
      focused={props.focused}
    >
      <Stack gap={2}>
        <div style={FORM_STYLE}>
          <TextInput
            label={t("admin.gtcc.dplane.builders.field.host")}
            value={hostId}
            onChange={(event) => setHostId(event.target.value)}
            data-testid="gtcc-dplane-builder-create-host"
          />
          <TextInput
            label={t("admin.gtcc.dplane.builders.field.instance")}
            value={instanceId}
            onChange={(event) => setInstanceId(event.target.value)}
            data-testid="gtcc-dplane-builder-create-instance"
          />
          <TextInput
            label={t("admin.gtcc.dplane.builders.field.database_name")}
            value={name}
            onChange={(event) => setName(event.target.value)}
            data-testid="gtcc-dplane-builder-create-name"
          />
          <div>
            <label htmlFor="gtcc-dplane-builder-create-purpose" style={SUB_STYLE}>
              {t("admin.gtcc.dplane.builders.field.purpose")}
            </label>
            <select
              id="gtcc-dplane-builder-create-purpose"
              data-testid="gtcc-dplane-builder-create-purpose"
              style={SELECT_STYLE}
              value={purpose}
              onChange={(event) => setPurpose(event.target.value as DbOpsDatabasePurpose)}
            >
              {DATABASE_PURPOSES.map((option) => (
                <option key={option} value={option}>
                  {t(`admin.gtcc.dplane.database_purpose.${option}`)}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div>
          <Button
            variant="secondary"
            size="sm"
            disabled={busy || !ready}
            onClick={() => void onValidate()}
            data-testid="gtcc-dplane-builder-create-validate"
            /*
             * Alle drei Builder tragen sichtbar dasselbe `[Validate]` wie die
             * Referenz. Damit die Schaltflaechen auch ohne Blick auf die Karte
             * unterscheidbar bleiben, nennt der zugaengliche Name zusaetzlich
             * den Plan; der sichtbare Text bleibt Teil des Namens.
             */
            aria-label={t("admin.gtcc.dplane.builders.validate.aria", {
              plan: t("admin.gtcc.dplane.builders.create"),
            })}
          >
            {t("admin.gtcc.dplane.builders.validate")}
          </Button>
        </div>
        <ValidationOutcome result={result} testId="gtcc-dplane-builder-create" />
      </Stack>
    </BuilderPanel>
  );
}

/* --------------------------------------------------------- Assignment */

function AssignmentBuilder(props: { focused: boolean; tenantId: string | undefined }): ReactNode {
  const { t } = useLocale();
  const [tenantId, setTenantId] = useState(props.tenantId ?? "");
  const [databaseId, setDatabaseId] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<DplaneResult<DbOpsPersistedPlan> | undefined>(undefined);

  const ready = tenantId.trim() !== "" && databaseId.trim() !== "";

  async function onValidate(): Promise<void> {
    setBusy(true);
    const next = await validateDplaneAssignmentPlan({
      tenantId: tenantId.trim(),
      targetDatabaseId: databaseId.trim(),
    });
    setResult(next);
    setBusy(false);
  }

  return (
    <BuilderPanel
      title={t("admin.gtcc.dplane.builders.assign")}
      note={t("admin.gtcc.dplane.builders.assign.hint")}
      testId="gtcc-dplane-builder-assign"
      focused={props.focused}
    >
      <Stack gap={2}>
        <div style={FORM_STYLE}>
          <TextInput
            label={t("admin.gtcc.dplane.builders.field.tenant")}
            value={tenantId}
            onChange={(event) => setTenantId(event.target.value)}
            data-testid="gtcc-dplane-builder-assign-tenant"
          />
          <TextInput
            label={t("admin.gtcc.dplane.builders.field.target_database")}
            value={databaseId}
            onChange={(event) => setDatabaseId(event.target.value)}
            data-testid="gtcc-dplane-builder-assign-database"
          />
        </div>
        <div>
          <Button
            variant="secondary"
            size="sm"
            disabled={busy || !ready}
            onClick={() => void onValidate()}
            data-testid="gtcc-dplane-builder-assign-validate"
            aria-label={t("admin.gtcc.dplane.builders.validate.aria", {
              plan: t("admin.gtcc.dplane.builders.assign"),
            })}
          >
            {t("admin.gtcc.dplane.builders.validate")}
          </Button>
        </div>
        <ValidationOutcome result={result} testId="gtcc-dplane-builder-assign" />
      </Stack>
    </BuilderPanel>
  );
}

/* --------------------------------------------------------------- Move */

function MoveBuilder(props: { focused: boolean; tenantId: string | undefined }): ReactNode {
  const { t } = useLocale();
  const [moveType, setMoveType] = useState<DbOpsMovePlanType>("cross_host_internal_vps_move");
  const [sourceHostId, setSourceHostId] = useState("");
  const [sourceInstanceId, setSourceInstanceId] = useState("");
  const [sourceDatabaseId, setSourceDatabaseId] = useState("");
  const [targetHostId, setTargetHostId] = useState("");
  const [targetInstanceId, setTargetInstanceId] = useState("");
  const [tenantId, setTenantId] = useState(props.tenantId ?? "");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<DplaneResult<DbOpsPersistedPlan> | undefined>(undefined);

  // The Health Center links "[Move-Plan (dry-run)]" per tenant; a later click
  // on another tenant must land in the same field.
  useEffect(() => {
    if (props.tenantId !== undefined) setTenantId(props.tenantId);
  }, [props.tenantId]);

  const ready =
    sourceHostId.trim() !== "" &&
    sourceInstanceId.trim() !== "" &&
    sourceDatabaseId.trim() !== "" &&
    targetHostId.trim() !== "" &&
    targetInstanceId.trim() !== "";

  async function onValidate(): Promise<void> {
    setBusy(true);
    const next = await validateDplaneMovePlan({
      moveType,
      sourceHostId: sourceHostId.trim(),
      sourceInstanceId: sourceInstanceId.trim(),
      sourceDatabaseId: sourceDatabaseId.trim(),
      targetHostId: targetHostId.trim(),
      targetInstanceId: targetInstanceId.trim(),
      ...(tenantId.trim() === "" ? {} : { tenantId: tenantId.trim() }),
    });
    setResult(next);
    setBusy(false);
  }

  return (
    <BuilderPanel
      title={t("admin.gtcc.dplane.builders.move")}
      note={t("admin.gtcc.dplane.builders.move.hint")}
      testId="gtcc-dplane-builder-move"
      focused={props.focused}
    >
      <Stack gap={2}>
        <div style={FORM_STYLE}>
          <div>
            <label htmlFor="gtcc-dplane-builder-move-type" style={SUB_STYLE}>
              {t("admin.gtcc.dplane.builders.field.move_type")}
            </label>
            <select
              id="gtcc-dplane-builder-move-type"
              data-testid="gtcc-dplane-builder-move-type"
              style={SELECT_STYLE}
              value={moveType}
              onChange={(event) => setMoveType(event.target.value as DbOpsMovePlanType)}
            >
              {MOVE_TYPES.map((option) => (
                <option key={option} value={option}>
                  {t(`admin.gtcc.dplane.move_type.${option}`)}
                </option>
              ))}
            </select>
          </div>
          <TextInput
            label={t("admin.gtcc.dplane.builders.field.tenant")}
            value={tenantId}
            onChange={(event) => setTenantId(event.target.value)}
            data-testid="gtcc-dplane-builder-move-tenant"
          />
          <TextInput
            label={t("admin.gtcc.dplane.builders.field.source_host")}
            value={sourceHostId}
            onChange={(event) => setSourceHostId(event.target.value)}
            data-testid="gtcc-dplane-builder-move-source-host"
          />
          <TextInput
            label={t("admin.gtcc.dplane.builders.field.source_instance")}
            value={sourceInstanceId}
            onChange={(event) => setSourceInstanceId(event.target.value)}
            data-testid="gtcc-dplane-builder-move-source-instance"
          />
          <TextInput
            label={t("admin.gtcc.dplane.builders.field.source_database")}
            value={sourceDatabaseId}
            onChange={(event) => setSourceDatabaseId(event.target.value)}
            data-testid="gtcc-dplane-builder-move-source-database"
          />
          <TextInput
            label={t("admin.gtcc.dplane.builders.field.target_host")}
            value={targetHostId}
            onChange={(event) => setTargetHostId(event.target.value)}
            data-testid="gtcc-dplane-builder-move-target-host"
          />
          <TextInput
            label={t("admin.gtcc.dplane.builders.field.target_instance")}
            value={targetInstanceId}
            onChange={(event) => setTargetInstanceId(event.target.value)}
            data-testid="gtcc-dplane-builder-move-target-instance"
          />
        </div>
        <div>
          <Button
            variant="secondary"
            size="sm"
            disabled={busy || !ready}
            onClick={() => void onValidate()}
            data-testid="gtcc-dplane-builder-move-validate"
            aria-label={t("admin.gtcc.dplane.builders.validate.aria", {
              plan: t("admin.gtcc.dplane.builders.move"),
            })}
          >
            {t("admin.gtcc.dplane.builders.validate")}
          </Button>
        </div>
        <ValidationOutcome result={result} testId="gtcc-dplane-builder-move" />
      </Stack>
    </BuilderPanel>
  );
}

/* ------------------------------------------------------ Plan-Historie */

function PlanTimeline(props: { planId: string }): ReactNode {
  const { t } = useLocale();
  const query = useQuery({
    queryKey: ["gtcc", "dplane", "plan-timeline", props.planId],
    queryFn: ({ signal }) => fetchDplanePlanTimeline(props.planId, signal),
    ...DPLANE_QUERY_OPTIONS,
  });
  return (
    <DplaneGate
      query={query}
      testId="gtcc-dplane-plan-timeline-panel"
      render={(raw: ReadonlyArray<DbOpsPersistedAuditEvent>) => {
        const events = asList(raw);
        return events.length === 0 ? (
          <NotMeasurable testId="gtcc-dplane-plan-timeline-empty" />
        ) : (
          <ol style={{ margin: 0, paddingInlineStart: "var(--gt-sys-space-4)" }}>
            {events.map((event) => (
              <li key={event.id} data-testid={`gtcc-dplane-plan-timeline-${event.id}`}>
                <Mono>
                  {t("admin.gtcc.dplane.history.event", {
                    kind: event.kind,
                    actor: event.actorRef,
                    observedAt: event.observedAt,
                  })}
                </Mono>
              </li>
            ))}
          </ol>
        );
      }}
    />
  );
}

function PlanHistoryPanel(props: { focused: boolean }): ReactNode {
  const { t } = useLocale();
  const [openPlanId, setOpenPlanId] = useState<string | null>(null);
  const query = useQuery({
    queryKey: DPLANE_PLANS_KEY,
    queryFn: ({ signal }) => fetchDplanePlans(signal),
    ...DPLANE_QUERY_OPTIONS,
  });

  return (
    <div data-testid="gtcc-dplane-history-anchor" data-focused={props.focused ? "true" : "false"}>
      <DplanePanel
        title={t("admin.gtcc.dplane.builders.history")}
        note={t("admin.gtcc.dplane.history.note")}
        testId="gtcc-dplane-history"
      >
        <Stack gap={2}>
          <DplaneGate
            query={query}
            testId="gtcc-dplane-history-panel"
            render={(raw: ReadonlyArray<DbOpsPersistedPlan>) => {
              const plans = asList(raw);
              return plans.length === 0 ? (
                <NotMeasurable testId="gtcc-dplane-history-empty" />
              ) : (
                <div style={TABLE_WRAP_STYLE}>
                  <table style={TABLE_STYLE} data-testid="gtcc-dplane-history-table">
                    <caption style={{ ...MONO_STYLE, textAlign: "start" }}>
                      {t("admin.gtcc.dplane.history.caption", { count: plans.length })}
                    </caption>
                    <thead>
                      <tr>
                        <th scope="col" style={TABLE_HEAD_STYLE}>
                          {t("admin.gtcc.dplane.history.col.plan")}
                        </th>
                        <th scope="col" style={TABLE_HEAD_STYLE}>
                          {t("admin.gtcc.dplane.history.col.kind")}
                        </th>
                        <th scope="col" style={TABLE_HEAD_STYLE}>
                          {t("admin.gtcc.dplane.history.col.risk")}
                        </th>
                        <th scope="col" style={TABLE_HEAD_STYLE}>
                          {t("admin.gtcc.dplane.history.col.status")}
                        </th>
                        <th scope="col" style={TABLE_HEAD_STYLE}>
                          {t("admin.gtcc.dplane.history.col.timeline")}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {plans.map((plan) => (
                        <tr key={plan.id} data-testid={`gtcc-dplane-history-row-${plan.id}`}>
                          {/* `td` wie in der Referenz, kein Zeilenkopf. */}
                          <td style={{ ...TABLE_CELL_STYLE, ...HOST_STYLE }}>{plan.id}</td>
                          <td style={{ ...TABLE_CELL_STYLE, ...MONO_STYLE }}>
                            {t(`admin.gtcc.dplane.plan_kind.${plan.kind}`)}
                          </td>
                          <td style={TABLE_CELL_STYLE}>
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "var(--gt-sys-space-2)",
                              }}
                              data-tone={toneOfRisk(plan.riskLevel)}
                              data-testid={`gtcc-dplane-history-risk-${plan.id}`}
                            >
                              <FleetDot tone={toneOfRisk(plan.riskLevel)} />
                              {t(`admin.gtcc.dplane.risk.${plan.riskLevel}`)}
                            </span>
                          </td>
                          <td style={TABLE_CELL_STYLE}>
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "var(--gt-sys-space-2)",
                              }}
                              data-tone={toneOfPlanStatus(plan.status)}
                              data-testid={`gtcc-dplane-history-status-${plan.id}`}
                            >
                              <FleetDot tone={toneOfPlanStatus(plan.status)} />
                              {t(`admin.gtcc.dplane.plan_status.${plan.status}`)}
                            </span>
                          </td>
                          <td style={TABLE_CELL_STYLE}>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                setOpenPlanId((current) => (current === plan.id ? null : plan.id))
                              }
                              data-testid={`gtcc-dplane-history-timeline-${plan.id}`}
                            >
                              {t("admin.gtcc.dplane.builders.show_timeline")}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <GtccCapHint
                    limit={DPLANE_PLAN_LIMIT}
                    shown={plans.length}
                    testId="gtcc-dplane-history-cap"
                  />
                </div>
              );
            }}
          />
          {openPlanId === null ? null : <PlanTimeline planId={openPlanId} />}
        </Stack>
      </DplanePanel>
    </div>
  );
}

/* ------------------------------------------------------------------ tab */

export function PlansTab(props: PlansTabProps): ReactNode {
  const { t } = useLocale();
  return (
    <div style={GRID_COLUMN_STYLE} data-testid="gtcc-dplane-plans-grid">
      <FleetNote testId="gtcc-dplane-plans-readonly-note">
        {t("admin.gtcc.dplane.builders.execution_note")}
      </FleetNote>
      <CreateDatabaseBuilder focused={props.focusBuilder === "create"} />
      <AssignmentBuilder
        focused={props.focusBuilder === "assign"}
        tenantId={props.focusBuilder === "assign" ? props.focusTenantId : undefined}
      />
      <MoveBuilder
        focused={props.focusBuilder === "move"}
        tenantId={props.focusBuilder === "move" ? props.focusTenantId : undefined}
      />
      <PlanHistoryPanel focused={props.focusBuilder === "history"} />
    </div>
  );
}
